import { GlobalStore, ApiResponse } from '@roc/feature-app-core';
import axios from 'axios';
import { action, makeObservable, observable, flow, computed } from 'mobx';
import { OneToolService } from '../../services/oneToolService';
import { ClosingFormStore } from './closingFormStore';
import { QuoteTypeStore } from '../quoteTypeStore';
import { format, isValid } from 'date-fns';
import { ELMSURE, isFixAndFlip, isGroundUp, isNotBlank, LoanSubType, NONE, OTHER, parseDate, stateMapping, TERM } from '@roc/feature-utils';
import { InsuranceFormStore } from './insuranceFormStore';
import { TitleFormStore } from './titleFormStore';

export class ClosingStore {
  private globalStore: GlobalStore;
  private oneToolService: OneToolService;
  public closingFormStore: ClosingFormStore;
  public insuranceFormStore: InsuranceFormStore;
  public titleFormStore: TitleFormStore;
  public attorneys: any[] = [];
  public titleProviders: any[] = [];
  public selectedTitleAgencyId: number | null = null;
  quoteTypeStore: QuoteTypeStore;

  constructor(globalStore, quoteTypeStore) {
    this.globalStore = globalStore;
    this.oneToolService = new OneToolService();
    this.closingFormStore = new ClosingFormStore(globalStore);
    this.insuranceFormStore = new InsuranceFormStore(globalStore);
    this.titleFormStore = new TitleFormStore(globalStore);
    this.quoteTypeStore = quoteTypeStore;
    makeObservable(this, {
      reset: action,
      closingFormStore: observable,
      getAttorneys: flow,
      getTitleProviders: flow,
      getClosingData: action,
      validateClosingData: action,
      setDefaultPreferredClosingAttorney: action,
      setSelectedTitleAgencyId: action,
      openTitleDialog: action,
      attorneys: observable,
      titleProviders: observable,
      selectedTitleAgencyId: observable,
      attorneyOptions: computed,
      brokerTitleContact: computed,
      titleCompanyOptions: computed,
    });
  }

  reset() {
    this.attorneys = [];
    this.titleProviders = [];
    this.selectedTitleAgencyId = null;
    this.closingFormStore.reset();
    this.insuranceFormStore.reset();
    this.titleFormStore.reset();
  }

  get brokerTitleContact(): { name: string; email: string } | null {
    const fields = this.quoteTypeStore.brokerStore?.brokerFormStore?.form?.fields;
    const email = (fields?.contactEmail?.value ?? '').trim();
    if (!fields?.hasBroker?.value || !email) return null;
    const name = [fields?.contactFirstName?.value, fields?.contactLastName?.value].filter(Boolean).join(' ')
      || fields?.companyName?.value || '';
    return { name, email };
  }

  setSelectedTitleAgencyId(name: string) {
    this.selectedTitleAgencyId = this.titleProviders.find(p => p.name === name)?.titleAgencyId ?? null;
  }

  openTitleDialog() {
    this.titleFormStore.openDialog();
    const lenderId = this.quoteTypeStore.lenderId;
    if (lenderId) {
      this.titleFormStore.fetchTitleCompanies(lenderId);
    }
  }

  loadClosingData(quoteData) {
    const { closingData } = quoteData;
    if (closingData) {
      const form = this.closingFormStore;
      form.loadForm({
        ...closingData,
        preferredInsurance: isNotBlank(closingData.preferredInsurance) ? closingData.preferredInsurance : ELMSURE,
        requestedClosingDate: parseDate(closingData.requestedClosingDate),
        originatorId: quoteData?.originatorId,
      });
      form.setEditMode(false);
      this.insuranceFormStore.loadForm(closingData?.otherInsuranceInfo || {});
      // Reconstruct V2 title selection from saved quote data.
      this.titleFormStore.loadTitleSelection(closingData);
      return form;
    }
  }

  getClosingData() {
    const values = this.closingFormStore.getFormValues();
    const fixAndFlipOrGroundUp = isFixAndFlip(this.quoteTypeStore.loanSubtype) || isGroundUp(this.quoteTypeStore.loanSubtype);
    const submitWithTamarisk = fixAndFlipOrGroundUp
      ? !!this.globalStore.userFeatures.tamariskAppraisal
      : false;

    // Determine title fields from V2 selection, falling back to the legacy dropdown path.
    const { selectedCompany } = this.titleFormStore;
    let preferredTitle = values?.preferredTitle === 'None' ? '' : (values?.preferredTitle ?? '');
    let selectedTitleAgencyId = this.selectedTitleAgencyId;
    let otherTitleInfo: any = {};

    if (selectedCompany && values?.preferredTitle !== 'None') {
      if (selectedCompany.titleAgencyId !== null) {
        // Existing agency: identified by selectedTitleAgencyId; contacts + communicateVia still persisted via otherTitleInfo.
        preferredTitle = selectedCompany.name;
        selectedTitleAgencyId = selectedCompany.titleAgencyId;
        otherTitleInfo = this.titleFormStore.getOtherTitleData();
        otherTitleInfo.includeBroker = !!this.brokerTitleContact && this.titleFormStore.includeBroker;
      } else {
        // New agency: backend creates it from otherTitleInfo.
        preferredTitle = OTHER;
        selectedTitleAgencyId = null;
        const mapped = this.titleFormStore.getOtherTitleData() as any;
        otherTitleInfo = mapped.name ? { ...mapped, includeBroker: !!this.brokerTitleContact && this.titleFormStore.includeBroker } : {};
      }
    }

    return {
      ...values,
      preferredTitle,
      requestedClosingDate: isValid(values?.requestedClosingDate)
        ? format(values.requestedClosingDate, 'MM/dd/yyyy')
        : null,
      otherInsuranceInfo: this.insuranceFormStore.getFormValues(),
      otherTitleInfo,
      selectedTitleAgencyId,
      submitAppraisalWithTamarisk: fixAndFlipOrGroundUp ? submitWithTamarisk : this.globalStore.userFeatures.tamariskAppraisal ? true : values.submitAppraisalWithTamarisk,
      originatorId: this.getLenderOriginatorId(values.originatorId),
      valuationQuestionnaire: this.quoteTypeStore.preflightStore.appraisalQuestionnaireFormStore.getValuationQuestionnaire(),
      sellAs: this.quoteTypeStore.isCorrespondentBetaFormEnabled ? this.mapSellAs(values.sellAs) : undefined,
    };
  }

  mapSellAs(participation) {
    return participation === 'Participate through life of loan' ? 'A-Note' : 'Whole Loan';
  }

  getLenderOriginatorId(selectedLenderOriginatorId) {
    return this.quoteTypeStore.userStore.isSeniorOriginator || this.quoteTypeStore.userStore.isJrOriginator ? this.quoteTypeStore.userStore.userInformation?.userId : selectedLenderOriginatorId;
  }

  fetchTitleCompaniesByState(states: string[]) {
    const state = states?.[0];
    if (state) {
      this.getTitleProviders(state);
    }
  }

  *getTitleProviders(state: string) {
    try {
      const response: ApiResponse = yield this.oneToolService.getTitleProvidersByState(state);
      this.titleProviders = response.data?.data ?? [];
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.log('Failed to fetch title providers, using default options.', error);
      }
    }
  }

  fetchAttorneysByState(states, allowedLoanType) {
    let finalState = states;
    if (this.quoteTypeStore.loanSubtype === LoanSubType.RENTAL_PORTFOLIO) {
      const stateLabel = this.quoteTypeStore.portfolioInformationFormStore.getFormValues().propertyStates
      const state = stateMapping.find((item) => item.label === stateLabel);
      finalState = [state.value];
    }
    this.getAttorneys(finalState, allowedLoanType);
  }

  *getAttorneys(states, allowedLoanType) {
    try {
      const economicsStore = this.quoteTypeStore.loanEconomicsStore;
      let response: ApiResponse
      if (allowedLoanType === TERM) {
        const { pricerLoanTerms } = this.quoteTypeStore.pricerSummaryStore;
        response = yield this.oneToolService.getLenderAttorneysByState(
          (states || []).join(','),
          pricerLoanTerms.amount
        );
      } else {
        response = yield this.oneToolService.getLenderAttorneysByState(
          (states || []).join(','),
          economicsStore.enableManualQuote ?
            economicsStore.manualExceptionQuoteFormStore.calculatedFields.totalLoanAmount :
            economicsStore.instaLeverageQuoteFormStore.calculatedFields.totalLoanAmount
        );
      }

      const attorneys = JSON.parse(response.data?.data);

      this.attorneys = attorneys?.filter(x => (x.externalAllowed && x.allowedLoanTypes.includes(allowedLoanType)))
        .sort((a1, a2) => a1.displayOrder - a2.displayOrder);

      this.setDefaultPreferredClosingAttorney(
        this.attorneys.find(attorney =>
          attorney?.company?.name?.includes('Internal')
        )?.company?.name
      );
    } catch (error) {
      console.log({ error });
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting attorneys.',
      });
    }
  }

  validateClosingData() {
    this.closingFormStore.runFormValidationWithMessage();
    return this.closingFormStore.form.meta.isValid;
  }

  setDefaultPreferredClosingAttorney(defaultAttorneyName?: string) {
    const currentAttorney = this.closingFormStore.form.fields
      .preferredClosingAttorney.value;
    if (defaultAttorneyName) {
      this.closingFormStore.onFieldChange(
        'preferredClosingAttorney',
        defaultAttorneyName
      );
      return;
    }
    if (
      isNotBlank(currentAttorney) && !this.isClosingAttorneyPresent(currentAttorney)
    ) {
      this.closingFormStore.onFieldChange(
        'preferredClosingAttorney',
        currentAttorney
      );
    }
  }

  private isClosingAttorneyPresent(preferredClosingAttorney) {
    return this.attorneys.some(
      attorney => attorney?.company?.name === preferredClosingAttorney
    );
  }

  get attorneyOptions() {
    return this.attorneys.map(attorney => ({
      id: attorney.investorAttorneyId,
      value: attorney.company.name,
      label: attorney.company.name,
    }));
  }

  get titleCompanyOptions() {
    const providerOptions = this.titleProviders.map(provider => ({
      value: provider.name,
      label: provider.name,
    }));
    return [
      ...providerOptions,
      { value: NONE, label: NONE },
      { value: OTHER, label: OTHER },
    ];
  }

  get canSubmitAppraisalWithTamarisk() {
    return !this.globalStore.userFeatures.isLenderOriginator &&
      !this.globalStore.userFeatures.isBroker &&
      !this.globalStore.userFeatures.tamariskAppraisal
  };

}
